import {Page, Text, View, Document, StyleSheet} from "@react-pdf/renderer"
import {PDFDownloadLink} from "@react-pdf/renderer"
import {ArrowDownToLine} from 'lucide-react';
import React from "react";
import {Tooltip} from "antd";

// Define styles with improved layout and readability
const styles = StyleSheet.create({
    page: {
        padding: 30,
        fontFamily: "Helvetica",
    },
    section: {
        marginBottom: 20,
        paddingBottom: 10,
        borderBottom: "1px solid #cccccc",
    },
    header: {
        fontSize: 16,
        fontWeight: "bold",
        marginBottom: 8,
        color: "#333333",
        textTransform: "uppercase",
    },
    subHeader: {
        fontSize: 14,
        fontWeight: "bold",
        marginBottom: 6,
        marginTop: 10,
        color: "#555555",
    },
    row: {
        flexDirection: "row",
        marginBottom: 4,
        flexWrap: "wrap",
    },
    label: {
        fontSize: 12,
        width: "30%",
        fontWeight: "bold",
    },
    value: {
        fontSize: 12,
        width: "70%",
        wordBreak: "break-word",
    },
    jsonContainer: {
        fontSize: 10,
        backgroundColor: "#f5f5f5",
        padding: 8,
        borderRadius: 4,
        marginTop: 4,
        marginBottom: 10,
        fontFamily: "Courier",
    },
    statusSuccess: {
        color: "#4CAF50",
        fontWeight: "bold",
    },
    statusFailed: {
        color: "#F44336",
        fontWeight: "bold",
    },
    statusPaid: {
        color: "#0066cc",
        fontWeight: "bold",
    },
    pageBreak: {
        height: 1,
        marginTop: 20,
        marginBottom: 20,
        pageBreakAfter: "always",
    },
    url: {
        color: "#0066cc",
        textDecoration: "underline",
    },
})

// Helper function to format JSON data with better readability and handle encoding issues
const formatJson = (data) => {
    if (!data) return "N/A"
    try {
        let jsonString

        // Parse the data if it's a string
        if (typeof data === "string") {
            // Try to parse it as JSON
            try {
                const parsed = JSON.parse(data)
                jsonString = JSON.stringify(parsed, null, 2)
            } catch (e) {
                // If parsing fails, just use the original string
                jsonString = data
            }
        } else {
            // If it's already an object, stringify it
            jsonString = JSON.stringify(data, null, 2)
        }

        // Clean up any potential encoding issues
        return jsonString.replace(/[\u0000-\u001F\u007F-\u009F\uFFFD]/g, "")
    } catch (error) {
        // If all else fails, convert to string and remove problematic characters
        return String(data).replace(/[\u0000-\u001F\u007F-\u009F\uFFFD]/g, "")
    }
}

// Helper function to safely encode text for PDF display
const safeEncodeText = (text) => {
    if (!text) return "N/A"

    // Convert to string if not already
    const stringText = String(text)

    // Replace problematic character sequences that appear in your output
    return stringText
        .replace(/5H030G/g, "") // Remove the problematic sequence in Payment Request
        .replace(/#A\?5H=>\s*\?>4B25@645=/g, "") // Remove the problematic sequence in Payment Response
        .replace(/[\u0000-\u001F\u007F-\u009F\uFFFD]/g, "") // Remove other control characters
}

// Helper function to determine status style
const getStatusStyle = (status) => {
    if (status === "SUCCESS" || status === "success" || status === "FISCALIZED" || status === true || status === "true") {
        return styles.statusSuccess
    } else if (status === "paid") {
        return styles.statusPaid
    } else {
        return styles.statusFailed
    }
}

// Main PDF Document Component
const MyDocumentLogs = ({logsData}) => (
    <Document>
        {/* First Page - General Information */}
        <Page size="A4" style={styles.page}>
            <View style={styles.section}>
                <Text style={styles.header}>Payment Log #{logsData.key}</Text>
            </View>

            <View style={styles.section}>
                <Text style={styles.subHeader}>General Information</Text>

                <View style={styles.row}>
                    <Text style={styles.label}>INN:</Text>
                    <Text style={styles.value}>{logsData.inn || "N/A"}</Text>
                </View>

                <View style={styles.row}>
                    <Text style={styles.label}>Device Serial:</Text>
                    <Text style={styles.value}>{logsData.deviceSerial || "N/A"}</Text>
                </View>

                <View style={styles.row}>
                    <Text style={styles.label}>Transaction ID:</Text>
                    <Text style={styles.value}>{logsData.transactionId || "N/A"}</Text>
                </View>

                <View style={styles.row}>
                    <Text style={styles.label}>Payment ID:</Text>
                    <Text style={styles.value}>{logsData.paymentId || "N/A"}</Text>
                </View>

                <View style={styles.row}>
                    <Text style={styles.label}>Amount:</Text>
                    <Text style={styles.value}>{logsData.amount || "N/A"}</Text>
                </View>

                <View style={styles.row}>
                    <Text style={styles.label}>Phone Number:</Text>
                    <Text style={styles.value}>{logsData.phoneNumber || "N/A"}</Text>
                </View>

                <View style={styles.row}>
                    <Text style={styles.label}>Log Type:</Text>
                    <Text style={styles.value}>{logsData.logType || "N/A"}</Text>
                </View>

                <View style={styles.row}>
                    <Text style={styles.label}>Created Date:</Text>
                    <Text style={styles.value}>{logsData.createdDate || "N/A"}</Text>
                </View>

                <View style={styles.row}>
                    <Text style={styles.label}>Status:</Text>
                    <Text style={[styles.value, getStatusStyle(logsData.status)]}>{logsData.status || "N/A"}</Text>
                </View>

                <View style={styles.row}>
                    <Text style={styles.label}>Success:</Text>
                    <Text
                        style={[styles.value, getStatusStyle(logsData.isSuccess)]}>{String(logsData.isSuccess) || "N/A"}</Text>
                </View>

                <View style={styles.row}>
                    <Text style={styles.label}>Fiscal URL:</Text>
                    <Text style={[styles.value, styles.url]}>{logsData.fiscalUrl || "N/A"}</Text>
                </View>
            </View>

            <View style={styles.pageBreak}/>
        </Page>

        {/* Second Page - Payment Details */}
        <Page size="A4" style={styles.page}>
            <View style={styles.section}>
                <Text style={styles.header}>Payment Details</Text>
            </View>

            <View style={styles.section}>
                <Text style={styles.subHeader}>Payment Headers</Text>
                <Text style={styles.jsonContainer}>{safeEncodeText(formatJson(logsData.paymentHeaders))}</Text>
            </View>

            <View style={styles.section}>
                <Text style={styles.subHeader}>Payment Request</Text>
                <Text style={styles.jsonContainer}>{safeEncodeText(formatJson(logsData.paymentRequest))}</Text>
            </View>

            <View style={styles.section}>
                <Text style={styles.subHeader}>Payment Response</Text>
                <Text style={styles.jsonContainer}>{safeEncodeText(formatJson(logsData.paymentResponse))}</Text>
            </View>

            <View style={styles.pageBreak}/>
        </Page>

        {/* Third Page - Confirmation Details */}
        <Page size="A4" style={styles.page}>
            <View style={styles.section}>
                <Text style={styles.header}>Confirmation Details</Text>
            </View>

            <View style={styles.section}>
                <Text style={styles.subHeader}>Confirmation Headers</Text>
                <Text style={styles.jsonContainer}>{safeEncodeText(formatJson(logsData.confirmHeaders))}</Text>
            </View>

            <View style={styles.section}>
                <Text style={styles.subHeader}>Confirmation Request</Text>
                <Text style={styles.jsonContainer}>{safeEncodeText(formatJson(logsData.confirmRequest))}</Text>
            </View>

            <View style={styles.section}>
                <Text style={styles.subHeader}>Confirmation Response</Text>
                <Text style={styles.jsonContainer}>{safeEncodeText(formatJson(logsData.confirmResponse))}</Text>
            </View>
        </Page>
    </Document>
)

// Main PDF Document Component
const MyDocumentS2PPaymeLogs = ({logsData}) => (
    <Document>
        {/* First Page - General Information */}
        <Page size="A4" style={styles.page}>
            <View style={styles.section}>
                <Text style={styles.header}>Payment Log #{logsData.key}</Text>
            </View>

            <View style={styles.section}>
                <Text style={styles.subHeader}>General Information</Text>

                <View style={styles.row}>
                    <Text style={styles.label}>Device Serial:</Text>
                    <Text style={styles.value}>{logsData.deviceSerial || "N/A"}</Text>
                </View>

                <View style={styles.row}>
                    <Text style={styles.label}>Transaction ID:</Text>
                    <Text style={styles.value}>{logsData.transactionId || "N/A"}</Text>
                </View>

                <View style={styles.row}>
                    <Text style={styles.label}>Payment ID:</Text>
                    <Text style={styles.value}>{logsData.paymentId || "N/A"}</Text>
                </View>

                <View style={styles.row}>
                    <Text style={styles.label}>Order ID:</Text>
                    <Text style={styles.value}>{logsData.orderId || "N/A"}</Text>
                </View>

                <View style={styles.row}>
                    <Text style={styles.label}>Amount (in tiyin):</Text>
                    <Text style={styles.value}>{String(logsData.amount) || "N/A"}</Text>
                </View>

                <View style={styles.row}>
                    <Text style={styles.label}>Amount (with tips):</Text>
                    <Text style={styles.value}>{logsData.amountWithTip || "N/A"}</Text>
                </View>

                <View style={styles.row}>
                    <Text style={styles.label}>Tip amount:</Text>
                    <Text style={styles.value}>{String(logsData.tipAmount) || "N/A"}</Text>
                </View>

                <View style={styles.row}>
                    <Text style={styles.label}>Tip percent:</Text>
                    <Text style={styles.value}>{String(logsData.tipPercent) || "N/A"}</Text>
                </View>

                <View style={styles.row}>
                    <Text style={styles.label}>Log Type:</Text>
                    <Text style={styles.value}>{logsData.paymentType || "N/A"}</Text>
                </View>

                <View style={styles.row}>
                    <Text style={styles.label}>Created Date:</Text>
                    <Text style={styles.value}>{logsData.createdDate || "N/A"}</Text>
                </View>

                <View style={styles.row}>
                    <Text style={styles.label}>Status:</Text>
                    <Text style={[styles.value, getStatusStyle(logsData.status)]}>{logsData.status || "N/A"}</Text>
                </View>

                <View style={styles.row}>
                    <Text style={styles.label}>Success:</Text>
                    <Text
                        style={[styles.value, getStatusStyle(logsData.isSuccess)]}>{String(logsData.isSuccess) || "N/A"}</Text>
                </View>

                <View style={styles.row}>
                    <Text style={styles.label}>State:</Text>
                    <Text style={styles.value}>{logsData.state || "N/A"}</Text>
                </View>

                <View style={styles.row}>
                    <Text style={styles.label}>Fiscal URL:</Text>
                    <Text style={[styles.value, styles.url]}>{logsData.fiscalUrl || "N/A"}</Text>
                </View>
            </View>

            <View style={styles.pageBreak}/>
        </Page>

        {/* Second Page - Payment Details */}
        <Page size="A4" style={styles.page}>
            <View style={styles.section}>
                <Text style={styles.header}>Payment Details</Text>
            </View>

            <View style={styles.section}>
                <Text style={styles.subHeader}>Prepare Request</Text>
                <Text style={styles.jsonContainer}>{safeEncodeText(formatJson(logsData.prepareRequest))}</Text>
            </View>

            <View style={styles.section}>
                <Text style={styles.subHeader}>Prepare Response</Text>
                <Text style={styles.jsonContainer}>{safeEncodeText(formatJson(logsData.prepareResponse))}</Text>
            </View>

            <View style={styles.section}>
                <Text style={styles.subHeader}>Complete Request</Text>
                <Text style={styles.jsonContainer}>{safeEncodeText(formatJson(logsData.completeRequest))}</Text>
            </View>

            <View style={styles.section}>
                <Text style={styles.subHeader}>Complete Response</Text>
                <Text style={styles.jsonContainer}>{safeEncodeText(formatJson(logsData.completeResponse))}</Text>
            </View>

            <View style={styles.pageBreak}/>
        </Page>

        {/* Third Page - Confirmation Details */}
        <Page size="A4" style={styles.page}>
            <View style={styles.section}>
                <Text style={styles.header}>Fiscalization Details</Text>
            </View>

            <View style={styles.section}>
                <Text style={styles.subHeader}>Fiscalization Request</Text>
                <Text style={styles.jsonContainer}>{safeEncodeText(formatJson(logsData.fiscalizationRequest))}</Text>
            </View>

            <View style={styles.section}>
                <Text style={styles.subHeader}>Fiscalization Response</Text>
                <Text style={styles.jsonContainer}>{safeEncodeText(formatJson(logsData.fiscalizationResponse))}</Text>
            </View>
        </Page>
    </Document>
)

const LogsType = ({logsData, type}) => {
    switch (type) {
        case "s2pLogs":
            return <MyDocumentS2PPaymeLogs logsData={logsData}/>;
        case "logs":
        default:
            return <MyDocumentLogs logsData={logsData}/>;
    }
};

// Component to generate and download the PDF
export const DownloadPDF = ({logsData, type = "logs"}) => (
    <Tooltip title="Download as PDF">
        <PDFDownloadLink
            document={<LogsType logsData={logsData} type={type}/>}
            fileName={`payment_${type}_${logsData?.key || "unknown"}.pdf`}
        >
            {({loading}) => (loading ? "Generating PDF..." : <ArrowDownToLine size={24}/>)}
        </PDFDownloadLink>
    </Tooltip>
);